// =============================================
// AI Suggest bio — Claude Haiku helper dla Person.description.
// Bierze core_entities + macro_statement + public_author_label + author_job_title,
// zwraca 2-3 zdania bio. Koszt ~$0.001/call.
// =============================================

import { getLLMProvider } from "@/services/llm";
import { logPublic, logDebug } from "@/services/contentEngine/contentLogger";

interface BioSuggestInput {
  publicAuthorLabel: string;
  authorJobTitle: string;
  mapMacroStatement: string;
  coreEntities: string[];
  knowsAbout: string[];
}

export async function suggestAuthorBio(input: BioSuggestInput): Promise<string> {
  const { publicAuthorLabel, authorJobTitle, mapMacroStatement, coreEntities, knowsAbout } = input;

  if (!publicAuthorLabel) {
    throw new Error("Brak public_author_label — uzupełnij imię i nazwisko autora");
  }

  const topics = [...new Set([...(knowsAbout || []), ...(coreEntities || [])])].slice(0, 5);

  const prompt = `Jesteś redaktorem przygotowującym krótkie bio autora na stronę WWW dla SEO + schema.org.

Autor: ${publicAuthorLabel}
${authorJobTitle ? `Stanowisko/rola: ${authorJobTitle}` : ""}
${mapMacroStatement ? `Domena tematyczna: ${mapMacroStatement}` : ""}
${topics.length > 0 ? `Specjalizacje: ${topics.join(", ")}` : ""}

Zadanie: Napisz 2-3 zdania bio autora w pierwszej lub trzeciej osobie (wybierz spójnie).
Cel: Person.description w schema.org + blok autora na stronie.

Wymagania:
- 150-400 znaków (MAX 500 znaków razem ze znakami interpunkcyjnymi)
- konkretne: czym się zajmuje, dla kogo pisze, z jakiej perspektywy
- zero ogólników typu "pasjonat", "miłośnik", "od lat związany z branżą"
- zero inwencji (nie wymyślaj certyfikatów, lat doświadczenia, klientów, nagród)
- ton rzeczowy, nie marketingowy
- NIE używaj hashtagów, emoji, kursywy

Output: TYLKO treść bio jako jeden paragraf HTML. Bez <p> tagu, bez nagłówków, bez komentarzy.`;

  logDebug("SCHEMA-BIO", `Generating bio for "${publicAuthorLabel}"`);

  try {
    const provider = getLLMProvider("claude");
    const response = await provider.call(prompt, {
      model: "claude-haiku-4-5-20251001",
      temperature: 0.7,
      step: "schema_author_bio",
    });
    const bio = (response.text || "").trim();
    logPublic("SCHEMA-BIO", `Generated bio ${bio.length} chars — $${(response.costUsd || 0).toFixed(4)}`);
    if (!bio) throw new Error("LLM zwrócił pusty bio");
    return bio;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    logPublic("SCHEMA-BIO", `Failed: ${msg}`);
    throw e;
  }
}
